import { useCallback, useEffect, useRef, useState } from 'react';
import { searchMindMaps } from '../../utils/mindmap/apiClient';

const EMPTY_RESULTS = Object.freeze([]);
const empty = (query, accountId) => ({ query, accountId, results: EMPTY_RESULTS, nextCursor: null, loading: false, error: '', incomplete: false });

export default function useGlobalMapSearch({ query, enabled, accountId }) {
  const [state, setState] = useState(() => empty('', accountId));
  const requestRef = useRef(null);
  const fetchPages = useCallback(async (text, cursor = null, previous = []) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setState(current => ({ ...(current.query === text && current.accountId === accountId ? current : empty(text, accountId)), loading: true, error: '' }));
    try {
      let results = [...previous];
      let nextCursor = cursor;
      let incomplete = false;
      // Continue through empty batches, with a visible continuation control
      // rather than an unbounded request chain for very large libraries.
      for (let page = 0; page < 4; page += 1) {
        const response = await searchMindMaps(text, { cursor: nextCursor, signal: controller.signal });
        results = [...results, ...(response.results || [])];
        nextCursor = response.nextCursor;
        incomplete ||= Boolean(response.unavailableDocuments || response.unavailableMaps);
        if (!nextCursor || results.length - previous.length >= 20) break;
      }
      if (requestRef.current !== controller || controller.signal.aborted) return;
      setState(current => ({ query: text, accountId, results, nextCursor, loading: false, error: '',
        incomplete: incomplete || (current.query === text && current.incomplete) }));
    } catch (error) {
      if (controller.signal.aborted || requestRef.current !== controller) return;
      setState(current => ({ ...current, loading: false,
        error: 'Could not search all maps. Current-map results are still available.' }));
    }
  }, [accountId]);

  useEffect(() => {
    requestRef.current?.abort();
    setState(empty(query, accountId));
    if (!enabled || query.trim().length < 2 || query.trim().length > 200) return undefined;
    const timer = setTimeout(() => fetchPages(query), 300);
    return () => { clearTimeout(timer); requestRef.current?.abort(); };
  }, [query, enabled, accountId, fetchPages]);

  const loadMore = useCallback(() => {
    if (state.nextCursor && !state.loading && state.query === query) {
      fetchPages(query, state.nextCursor, state.results);
    }
  }, [fetchPages, query, state]);
  return { ...(enabled && state.query === query && state.accountId === accountId ? state : empty(query, accountId)), loadMore };
}
